import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchQuestions } from '../api/quizApi';
import { Question, QuizState, CategoryCounts } from '../types';
import { AppDispatch } from '../store';
import {
  dataReceived,
  dataFailed,
  selectQuestions,
  selectStatus,
  selectIndex,
  selectAnswer,
  selectCategoryCounts,
  selectAnswers,
  selectNumQuestions,
  selectActiveQuestion,
  selectResultsSubmitted,
} from '../features/quiz/quizSlice';

interface QuizHookResult {
  questions: Question[];
  status: QuizState['status'];
  index: number;
  answer: number | null;
  categoryCounts: CategoryCounts;
  answers: number[];
  numQuestions: number;
  activeQuestion: Question | undefined;
  resultsSubmitted: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useQuiz(): QuizHookResult {
  const dispatch = useDispatch<AppDispatch>();

  // Get all the state from Redux
  const questions = useSelector(selectQuestions);
  const status = useSelector(selectStatus);
  const index = useSelector(selectIndex);
  const answer = useSelector(selectAnswer);
  const categoryCounts = useSelector(selectCategoryCounts);
  const answers = useSelector(selectAnswers);
  const numQuestions = useSelector(selectNumQuestions);
  const activeQuestion = useSelector(selectActiveQuestion);
  const resultsSubmitted = useSelector(selectResultsSubmitted);

  // Use React Query to fetch questions
  const { isLoading, error, data } = useQuery<Question[], Error>({
    queryKey: ['questions'],
    queryFn: fetchQuestions,
    refetchOnWindowFocus: false,
    retry: 1
  });

  // Handle success and error with useEffect
  useEffect(() => {
    if (data) {
      dispatch(dataReceived(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(dataFailed());
    }
  }, [error, dispatch]);

  return {
    questions,
    status,
    index,
    answer,
    categoryCounts,
    answers,
    numQuestions,
    activeQuestion,
    resultsSubmitted,
    isLoading,
    error: error as Error | null,
  };
}
